import React, { FunctionComponent, ReactElement, useState } from 'react';
import { Transition } from 'react-transition-group';
import { FormattedMessage } from 'react-intl';
import { History } from 'history';
import styled from 'styled-components';
import ReactDOM from 'react-dom';
import ScrollMenu from 'react-horizontal-scrolling-menu';
import smoothscroll from 'smoothscroll-polyfill';
import { Image } from '@empiriecom/module-components/Image';
import useTracking, { GtmGenericEvent } from '@empiriecom/module-components/hooks/useTracking';
import { ColorPicker } from '@/src/components/icons';
import { DimensionValue, Dimension } from '@/src/interfaces';
import { GtmEventUserInteraction } from '@/src/components/Tracking/types';
import usePage from '../ProductContext/hooks/usePage';

const overlayTransitionStyles = {
  entered: { display: 'block', opacity: 1, pointerEvents: 'all' },
  entering: { display: 'block', opacity: 0, pointerEvents: 'none' },
  exited: { display: 'block', opacity: 0, pointerEvents: 'none' },
  exiting: { display: 'block', opacity: 1, pointerEvents: 'all' },
};

const pickerTransitionStyles = {
  entered: { transform: 'translateY(0)' },
  entering: { transform: 'translateY(100%)' },
  exited: { transform: 'translateY(100%)' },
  exiting: { transform: 'translateY(0)' },
};

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border: solid 1px #959a9c;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  overflow: hidden;
  width: 2.5rem;
  height: 2.5rem;
  cursor: pointer;
`;

const PickerWrapper = styled.div`
  display: block;
  position: fixed;
  bottom: 0;
  background-color: #fff;
  width: 100%;
  z-index: 1003;
  will-change: transform;
  transition: transform 0.3s;
  transition-timing-function: cubic-bezier(0.645, 0.045, 0.355, 1);
  transform: translateY(100%);
`;

const PickerOverlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: block;
  pointer-events: none;
  opacity: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1002;
  transition: opacity 0.3s;
  transition-timing-function: cubic-bezier(0.645, 0.045, 0.355, 1);
  will-change: opacity;
`;

const PickerHeadline = styled.div`
  font-size: 0.8125rem;
  margin: 1.5rem 0 1rem 1rem;
`;

const Variation = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 6rem;
  border-radius: 3px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2), 0 2px 1px -1px rgba(0, 0, 0, 0.12),
    0 1px 1px 0 rgba(0, 0, 0, 0.14);
  &&.selected {
    border: 2px solid #444444;
  }
  overflow: hidden;
  margin: 0.75rem 0.5rem 1rem 0;
  &&.first {
    margin-left: 1rem;
  }
`;

const VariationImageWrapper = styled.div`
  width: 6rem;
  height: 6rem;
`;

const VariationImage = styled(Image)`
  width: auto;
  height: 90%;
  margin: auto;
`;

const VariationName = styled.div`
  border-top: 1px solid #f4f4f4;
  text-align: center;
  padding: 0.75rem;
  font-size: 0.6875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const handleVariationClick = (
  { isPickerOpen, setIsOverlayOpen, setVariation, variation, history, isBot },
  dispatchGTMEvent: <T extends GtmGenericEvent>(event: T) => void,
) => {
  const { sku, isSelected } = variation;
  if (isPickerOpen) {
    if (typeof window !== 'undefined') {
      window.scroll({ behavior: 'smooth', left: 0, top: 0 });
    }
  }
  setTimeout(() => setIsOverlayOpen(false), 400);
  if (!isSelected) {
    setTimeout(() => {
      setVariation(sku, history, isBot);
      dispatchGTMEvent<GtmEventUserInteraction>({
        event: 'ga_event',
        eventAction: 'rainbowpicker',
        eventCategory: 'userInteraction',
        eventLabel: 'rainbowpicker',
        eventValue: 'variationChange',
      });
    }, 550);
  } else {
    dispatchGTMEvent<GtmEventUserInteraction>({
      event: 'ga_event',
      eventAction: 'rainbowpicker',
      eventCategory: 'userInteraction',
      eventLabel: 'rainbowpicker',
      eventValue: 'close',
    });
  }
};

export interface PickerProps {
  /** The dimension to render. */
  dimension: Dimension;
  /** Handler to change the selected variation on click. */
  setVariation: (varId: string, history?: History<any>) => void;
  /** The history. */
  history: History;
}

/**
 * <p>This is the picker component. It represents an additional variation selection
 * which can be used to switch variations.</p>
 * <p>The picker can be opened via clicking the icon. The click opens a fullscreen overlay. This overlay
 * contains a swipeable slider with all available variations for the given dimension. Clicking the overlay
 * background results in closing the overlay without any changes to the currently selected variation.</p>
 * <p>Each variation is rendered as a tile. A click on the tile triggers a variation change via the given
 * `setVariation` function and closes the overlay. If the window scroll position is `> 0` a smooth scroll animation
 * is triggered to scroll the window to `0`. This is a convenience feature to get the newly loaded product
 * image into the viewport.</p>
 * <p>If the currently selected variation is clicked only the close animation will be triggered, however no
 * call for `setVariation` is done.</p>
 */
export const Picker: FunctionComponent<PickerProps> = ({
  dimension,
  setVariation,
  history,
}): ReactElement => {
  const [isPickerOpen, setIsPickerOpen] = useState<boolean>(false);
  const [isOverlayOpen, setIsOverlayOpen] = useState<boolean>(false);
  const [selected, setSelected] = useState<string>();
  const dispatchGTMEvent = useTracking();
  const { isBot } = usePage();
  try {
    smoothscroll.polyfill();
  } catch (e) {
    // ignore error (should only happen on serverside)
  }

  const pickerNav =
    dimension.values &&
    dimension.values.map((variation: DimensionValue, index: number) => {
      let className = '';
      if (index === 0) {
        className = className.concat(' first');
      }
      if (variation.isSelected) {
        className = className.concat(' selected');
      }
      return (
        <Variation
          data-testid="picker-variation"
          key={variation.sku}
          id={variation.isSelected ? 'selected' : undefined}
          onClick={() =>
            handleVariationClick(
              {
                history,
                isPickerOpen,
                setIsOverlayOpen,
                setVariation,
                variation,
                isBot,
              },
              dispatchGTMEvent,
            )
          }
          className={className}
        >
          <VariationImageWrapper>
            {variation.variationMainImage ? (
              <VariationImage
                src={`${variation.variationMainImage}?$PDV_gallery$`}
                alt={variation.displayText}
                fallback={() => null}
              />
            ) : (
              variation.rgb && (
                <div
                  style={{
                    backgroundColor: `#${variation.rgb}`,
                    height: '100%',
                    width: '100%',
                  }}
                  title={variation.displayText}
                />
              )
            )}
          </VariationImageWrapper>
          <VariationName>{variation.displayText}</VariationName>
        </Variation>
      );
    });

  return (
    pickerNav !== null && (
      <>
        <IconWrapper
          onClick={() => {
            setIsOverlayOpen(true);
            dispatchGTMEvent<GtmEventUserInteraction>({
              event: 'ga_event',
              eventAction: 'rainbowpicker',
              eventCategory: 'userInteraction',
              eventLabel: 'rainbowpicker',
              eventValue: 'open',
            });
          }}
          data-testid="picker-icon"
        >
          <ColorPicker size="2rem" />
        </IconWrapper>
        {ReactDOM.createPortal(
          <>
            <Transition in={isOverlayOpen} timeout={isPickerOpen ? 150 : 0}>
              {(state: string) => (
                <PickerOverlay
                  data-testid="picker-overlay"
                  style={{ ...overlayTransitionStyles[state] }}
                  onClick={() => {
                    setIsOverlayOpen(false);
                    dispatchGTMEvent<GtmEventUserInteraction>({
                      event: 'ga_event',
                      eventAction: 'rainbowpicker',
                      eventCategory: 'userInteraction',
                      eventLabel: 'rainbowpicker',
                      eventValue: 'close',
                    });
                  }}
                />
              )}
            </Transition>
            <Transition
              in={isOverlayOpen}
              timeout={isPickerOpen ? 0 : 150}
              onEntered={() => setIsPickerOpen(true)}
              onExited={() => setIsPickerOpen(false)}
            >
              {(state: string) => (
                <PickerWrapper
                  style={{ ...pickerTransitionStyles[state] }}
                  data-testid="picker-wrapper"
                >
                  <PickerHeadline>
                    <FormattedMessage
                      id="color-picker.headline"
                      defaultMessage="{axisname} wählen"
                      values={{ axisname: dimension.displayName }}
                    />
                  </PickerHeadline>
                  <ScrollMenu
                    data={pickerNav}
                    alignCenter={false}
                    onSelect={(item: string) => setSelected(item)}
                    selected={selected}
                    scrollToSelected
                    transition={0.3}
                  />
                </PickerWrapper>
              )}
            </Transition>
          </>,
          typeof document !== 'undefined' && document.getElementsByTagName('body')[0],
        )}
      </>
    )
  );
};

export default Picker;
